// ColumnHeader Component
// This file defines a table column header with optional sorting and filtering controls.

import { ReactNode } from "react";
import { styled } from "@stitches/react";
import { Button } from "@/vendor/Forge/dist";
import FilterIcon from "@/components/Icons/filter";
import { SortOrder } from "@/types/order-table";
import SortIcon from "./sort-icon";

/**
 * Wrapper
 * Styled container for the column header, aligns title and controls.
 */
const Wrapper = styled("div", {
  display: "flex",
  padding: "0 5px",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
});

/**
 * TitleWrapper
 * Container for the column title.
 */
const TitleWrapper = styled("div", {});

/**
 * InteractionWrapper
 * Container for filter and sort controls.
 */
const InteractionWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
});

/**
 * Props for the ColumnHeader component
 * @param id - Unique identifier for the column
 * @param name - Column display name (can be string or ReactNode)
 * @param sortOrder - Current sort order (ASC, DESC, or null)
 * @param onSortClick - Handler for sort icon click
 * @param onFilterClick - Handler for filter icon click
 * @param isActiveFilter - Whether the filter is active
 */
interface Props {
  id: string;
  name: ReactNode;
  sortOrder?: SortOrder;
  onSortClick?: () => void;
  onFilterClick?: () => void;
  isActiveFilter?: boolean;
}

/**
 * ColumnHeader Component
 * Renders a table column header with optional filter and sort controls.
 *
 * @param id - Unique identifier for the column
 * @param name - Column display name
 * @param sortOrder - Current sort order
 * @param onSortClick - Handler for sort icon click
 * @param onFilterClick - Handler for filter icon click
 * @param isActiveFilter - Whether the filter is active
 */
const ColumnHeader = ({
  id,
  name,
  sortOrder = null,
  onSortClick,
  onFilterClick,
  isActiveFilter = false,
}: Props) => {
  return (
    <Wrapper data-testid="column-header">
      {/* Column title */}
      <TitleWrapper>{name}</TitleWrapper>
      <InteractionWrapper>
        {/* Filter button, shown if onFilterClick is provided */}
        {onFilterClick && (
          <Button data-testid={id} variant="tertiary" onClick={onFilterClick}>
            <FilterIcon active={isActiveFilter} />
          </Button>
        )}
        {/* Sort icon, shown if onSortClick is provided */}
        {onSortClick && (
          <SortIcon sortOrder={sortOrder} onIconClick={onSortClick} />
        )}
      </InteractionWrapper>
    </Wrapper>
  );
};

export default ColumnHeader;
